/**
 * DateInput - Component for date input with current position support
 */

import React, { useState, useEffect } from 'react';
import './DateInput.css';

const DateInput = ({ 
  id, 
  value, 
  onChange, 
  allowCurrent = false,
  className = '',
  ...props 
}) => {
  const [dateValue, setDateValue] = useState('');
  const [isCurrent, setIsCurrent] = useState(false);

  // Initialize from value
  useEffect(() => {
    if (value === null || value === '') {
      setIsCurrent(allowCurrent);
      setDateValue('');
    } else {
      setIsCurrent(false);
      // Handle different date formats and convert to YYYY-MM for input
      let dateToUse;
      
      if (typeof value === 'string') {
        // Handle YYYY-MM-DD format (from database)
        if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month] = value.split('-');
          setDateValue(`${year}-${month}`);
          return;
        }
        // Handle YYYY-MM format
        else if (value.match(/^\d{4}-\d{2}$/)) {
          setDateValue(value);
          return;
        }
        // Handle other string formats
        else {
          dateToUse = new Date(value);
        }
      } else {
        dateToUse = new Date(value);
      }
      
      if (dateToUse && !isNaN(dateToUse.getTime())) {
        const year = dateToUse.getFullYear();
        const month = (dateToUse.getMonth() + 1).toString().padStart(2, '0');
        setDateValue(`${year}-${month}`);
      } else {
        setDateValue('');
      }
    }
  }, [value, allowCurrent]);

  // Handle date input change
  const handleDateChange = (e) => {
    const newValue = e.target.value;
    setDateValue(newValue);
    
    if (newValue) {
      // Convert YYYY-MM to YYYY-MM-01 format (database expects YYYY-MM-DD)
      const [year, month] = newValue.split('-');
      const dateStr = `${year}-${month}-01`;
      onChange(dateStr);
      setIsCurrent(false);
    } else {
      onChange('');
    }
  };

  // Handle current checkbox change
  const handleCurrentChange = (e) => {
    const checked = e.target.checked;
    setIsCurrent(checked);
    
    if (checked) {
      setDateValue('');
      onChange(null); // null indicates current position
    } else {
      onChange('');
    }
  };

  // Format date for display
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      let date;
      
      // Handle YYYY-MM-DD format
      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month] = dateStr.split('-');
        date = new Date(year, month - 1); // month is 0-indexed in Date constructor
      } 
      // Handle YYYY-MM format
      else if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}$/)) {
        const [year, month] = dateStr.split('-');
        date = new Date(year, month - 1);
      }
      // Handle other formats
      else {
        date = new Date(dateStr);
      }
      
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long' 
        });
      }
      
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={`date-input-container ${className}`}>
      <div className="date-input-wrapper">
        <input
          id={id}
          type="month"
          value={dateValue}
          onChange={handleDateChange}
          disabled={isCurrent}
          className={`date-input ${isCurrent ? 'disabled' : ''}`}
          {...props}
        />
        
        {allowCurrent && (
          <div className="current-checkbox-wrapper">
            <label className="current-checkbox">
              <input
                type="checkbox"
                checked={isCurrent}
                onChange={handleCurrentChange}
              />
              <span className="checkbox-label">Current</span>
            </label>
          </div>
        )}
      </div>
      
      {value && !isCurrent && (
        <div className="date-display">
          {formatDateForDisplay(value)}
        </div>
      )}
      
      {isCurrent && (
        <div className="date-display current">
          Present
        </div>
      )}
    </div>
  );
};

export default DateInput;